/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import { clsx } from '@accelint/design-foundation/lib/utils';
import styles from './styles.module.css';
import type { DrawerMenuProps } from './types';

/**
 * DrawerMenu - Navigation menu for switching between drawer views.
 *
 * Contains DrawerMenuItem components. Renders as a semantic `<nav>` element.
 *
 * @param props - {@link DrawerMenuProps}
 * @param props.className - Optional CSS class name.
 * @param props.position - Position of the menu within the layout.
 * @param props.children - DrawerMenuItem components to render.
 * @returns The rendered DrawerMenu component.
 *
 * @example
 * ```tsx
 * <DrawerMenu position="center">
 *   <DrawerMenuItem for={viewA}>View A</DrawerMenuItem>
 *   <DrawerMenuItem for={viewB}>View B</DrawerMenuItem>
 * </DrawerMenu>
 * ```
 */
export function DrawerMenu({
  className,
  position = 'center',
  children,
  ...rest
}: DrawerMenuProps) {
  return (
    <nav {...rest} className={clsx(styles.menu, styles[position], className)}>
      {children}
    </nav>
  );
}
